import type { VerificationVideo } from "@sitescene/types";

import { DetailPanel, KeyValueList } from "@/components/detail-panel";

interface ClipPanelProps {
	/** Shown in place of the player when the record has no clip. */
	empty: string;
	error: Error | null;
	/**
	 * Whether there is a clip, for a caller that knows before the media key
	 * arrives (an event's key comes with its playback URL). Defaults to
	 * whether `mediaKey` is set.
	 */
	hasClip?: boolean;
	isLoading: boolean;
	mediaKey: string | undefined;
	testId: string;
	video: undefined | VerificationVideo;
}

/**
 * The clip itself, so the reviewer sees what the model saw before answering
 * for it. The URL is signed per view and expires, so the caller owns the
 * query that fetches it; this only renders the result, with the media key and
 * where in the file the detection sits.
 */
export const ClipPanel = ({
	empty,
	error,
	hasClip,
	isLoading,
	mediaKey,
	testId,
	video,
}: ClipPanelProps) => {
	if (!(hasClip ?? Boolean(mediaKey))) {
		return (
			<DetailPanel title="Clip">
				<p className="text-tertiary text-sm">{empty}</p>
			</DetailPanel>
		);
	}

	return (
		<DetailPanel title="Clip">
			{isLoading && (
				<p className="text-tertiary text-sm">Fetching playback URL…</p>
			)}
			{error && (
				<p className="text-error-primary text-sm" role="alert">
					Could not load the clip: {error.message}
				</p>
			)}
			{video && (
				<video
					className="bg-primary-solid max-h-[480px] w-full rounded"
					controls
					data-testid={testId}
					playsInline
					preload="metadata"
					src={video.url}
				/>
			)}
			<KeyValueList
				items={[
					...(mediaKey
						? [
								{
									label: "Media key",
									value: <code className="text-xs">{mediaKey}</code>,
								},
							]
						: []),
					...(video?.startTime
						? [
								{
									label: "Detection in clip",
									value: `${video.startTime}${video.stopTime ? ` – ${video.stopTime}` : ""}`,
								},
							]
						: []),
				]}
			/>
		</DetailPanel>
	);
};
